"use client";

import { Add2, Close, Remove } from "@ellaandil/ui";
import { clsx, DialogClose, Slot } from "@trydig/ui";
import { type ComponentProps, Suspense, startTransition, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { DrawerContent, DrawerRoot, DrawerViewport } from "@/components/ui/Drawer";
import { Typography } from "@/components/ui/Typography";
import { usePLP } from "@/features/products/ProductListingPage/hooks/PLPContext";
import { useTranslations } from "@/i18n/client";
import { useModal } from "@/lib/hooks/useModal";
import type { PLPSort } from "../types";
import { Filters } from "./Filters";

const useFilterData = () => {
	const plp = usePLP();
	const rawFilters = plp.data?.pages[0]?.filters ?? [];
	// Always render the canonical SSR-initial filter set. The current response
	// only tells us which options are *still available* given active filters;
	// options that drop out get visually disabled but stay visible so the user
	// can see the full possibility space.
	const filters = plp.initialPage.filters;
	// Per-group set of currently-available option values (positive count in the
	// active response). Pills outside this set become disabled in the UI.
	const availableByKey = useMemo(() => {
		const map: Record<string, Set<string>> = {};
		for (const g of rawFilters) {
			const set = new Set<string>();
			for (const o of g.options) {
				if ((o.count ?? 0) > 0) set.add(o.value);
			}
			map[g.key] = set;
		}
		return map;
	}, [rawFilters]);
	// `selectedByKey` comes straight from the URL via usePLPFilters, so it
	// updates synchronously when a checkbox flips. Using it as the source for
	// `useOptimistic` inside Filters means the optimistic state transitions
	// directly to the URL truth — no flicker waiting for the server refetch.
	const selectedByKey = plp.filters;
	const totalSelected = useMemo(
		() => Object.values(selectedByKey).reduce((sum, arr) => sum + arr.length, 0),
		[selectedByKey],
	);
	const sorts = plp.data?.pages[0]?.sorts ?? plp.initialPage.sorts ?? [];
	// Read total directly from the PLP query (with the SSR-seeded initialPage as
	// a fallback) so the "Show results" button has the right number on the very
	// first paint. The Jotai atom set by ProductList only updates after its
	// useEffect fires, which lands a frame too late and showed 0 here on mount.
	const total = plp.data?.pages[0]?.total ?? plp.initialPage.total ?? 0;
	return {
		filters,
		availableByKey,
		selectedByKey,
		toggleFilter: plp.toggleFilter,
		clear: plp.clear,
		totalSelected,
		total,
		isLoading: plp.isLoading || plp.data === undefined,
		// `isFetching` covers refetches after the SSR seed too — perfect for showing
		// a "Show results" loading state while a filter toggle is in flight.
		isFetching: plp.isFetching,
		sorts,
		sortId: plp.sortId,
		setSort: plp.setSort,
	};
};

const FooterActions = ({
	totalResults,
	hasSelections,
	isFetching,
	onClear,
	onShowResults,
	layout,
}: {
	totalResults: number;
	hasSelections: boolean;
	isFetching: boolean;
	onClear: () => void;
	onShowResults: () => void;
	layout: "horizontal" | "vertical";
}) => {
	const t = useTranslations();
	const showResults = (
		<Button
			variant="solid"
			color="primary"
			size="large"
			onClick={onShowResults}
			loading={isFetching}
			className={layout === "vertical" ? "w-full" : "flex-1"}
		>
			<span>{t("Show results")}</span>
			{/*
			 * `totalResults` comes from a Jotai atom mutated client-side. The
			 * atom keeps its last value across PLP visits, so SSR (always 0)
			 * and the first client render (whatever was remembered) can
			 * disagree. `suppressHydrationWarning` lets React replace the
			 * server snapshot silently on mount.
			 */}
			{!isFetching && (
				<sup className="ml-0.5 leading-none" suppressHydrationWarning>
					{totalResults}
				</sup>
			)}
		</Button>
	);
	const clearAll = (
		<Button
			variant="outline"
			color="secondary"
			size="large"
			onClick={onClear}
			disabled={!hasSelections}
			className={layout === "vertical" ? "w-full" : "flex-1"}
		>
			{t("Clear all")}
		</Button>
	);
	return (
		<div
			className={clsx(
				"shrink-0 flex gap-grid-component-spacing-md border-t border-border-medium",
				"py-grid-component-spacing-sm",
				layout === "vertical" ? "flex-col" : "flex-row px-grid-component-spacing-xl",
			)}
		>
			{layout === "vertical" ? (
				<>
					{showResults}
					{clearAll}
				</>
			) : (
				<>
					{clearAll}
					{showResults}
				</>
			)}
		</div>
	);
};

const SortAccordion = ({
	sorts,
	sortId,
	setSort,
}: {
	sorts: PLPSort[];
	sortId: string | undefined;
	setSort: (id: string | undefined) => void;
}) => {
	const t = useTranslations();
	const [isOpen, setIsOpen] = useState(false);
	// The first sort entry is Depict's default ranking ("Recommended"). When
	// the URL has no `?sort=` param, that's what's actually being shown — so
	// it doubles as the "active" sort and we keep the URL clean by clearing
	// the param when the user re-selects it.
	const defaultSort = sorts[0];
	const activeSort = sorts.find((s) => s.id === sortId) ?? defaultSort;
	const Icon = isOpen ? Remove : Add2;

	if (sorts.length === 0) return null;

	return (
		<div className="w-full border-b border-border-medium">
			<button
				type="button"
				onClick={() => setIsOpen((v) => !v)}
				aria-expanded={isOpen}
				className="flex w-full cursor-pointer items-center gap-grid-component-spacing-4xs py-grid-component-spacing-sm text-left"
			>
				<span className="flex flex-1 min-w-0 items-center gap-grid-component-spacing-4xs">
					<Typography as="span" variant="label-sm-reg" className="min-w-0 text-text-sand-text-4">
						{t("Sort by")}
						{activeSort ? `: ${activeSort.label}` : ""}
					</Typography>
				</span>
				<span
					aria-hidden
					className="inline-flex size-6 shrink-0 items-center justify-center rounded-radius-component-specific-button"
				>
					<Icon className="size-3.5 text-text-dark-text-1" />
				</span>
			</button>
			<section
				aria-hidden={!isOpen}
				className={clsx(
					"grid overflow-hidden transition-[grid-template-rows] duration-300",
					isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
				)}
			>
				<div className="min-h-0 overflow-hidden">
					<div className="py-grid-component-spacing-md flex flex-col gap-grid-component-spacing-md">
						{sorts.map((sort) => {
							const isDefault = sort.id === defaultSort?.id;
							return (
								<SortRadio
									key={sort.id}
									label={sort.label}
									isActive={activeSort?.id === sort.id}
									// Clear the URL param when picking the default — Depict already
									// returns this order without `?sort=`.
									onSelect={() => startTransition(() => setSort(isDefault ? undefined : sort.id))}
								/>
							);
						})}
					</div>
				</div>
			</section>
		</div>
	);
};

const SortRadio = ({
	label,
	isActive,
	onSelect,
}: {
	label: string;
	isActive: boolean;
	onSelect: () => void;
}) => (
	// biome-ignore lint/a11y/useSemanticElements: styled radio control — the visible affordance is a custom span dot, not a native input, and we preserve radio semantics via role + aria-checked.
	<button
		type="button"
		role="radio"
		aria-checked={isActive}
		onClick={onSelect}
		className="flex w-full cursor-pointer items-center gap-grid-component-spacing-2xs text-left"
	>
		<span
			aria-hidden
			className={clsx(
				"relative size-6 shrink-0 rounded-full border-[1.4px] bg-background-bg-neutral-1",
				isActive ? "border-button-solid-primary-default-bg" : "border-border-medium",
			)}
		>
			{isActive && (
				<span className="absolute inset-[16.67%] rounded-full bg-button-solid-primary-default-bg" />
			)}
		</span>
		<span className="flex-1 text-sm text-text-dark-text-1">{label}</span>
	</button>
);

const FilterDrawer = () => {
	const t = useTranslations();
	const { isOpen, setOpen, close } = useModal("filters");
	const {
		filters,
		availableByKey,
		selectedByKey,
		toggleFilter,
		clear,
		totalSelected,
		total,
		isLoading,
		isFetching,
		sorts,
		sortId,
		setSort,
	} = useFilterData();

	return (
		<DrawerRoot open={isOpen} onOpenChange={setOpen}>
			<DrawerViewport
				side="left"
				className="!max-w-[600px] bg-background-bg-sand-1 backdrop:bg-overlay-3 backdrop:backdrop-blur-[2.5px]"
			>
				<DrawerContent className="!px-0 !gap-0 h-full flex flex-col">
					<header className="shrink-0 flex h-12 items-center px-grid-component-spacing-xl">
						<div className="flex h-full flex-1 items-center justify-between border-b border-border-medium">
							<Typography variant="body-xs-reg" as="h2" className="text-text-dark-text-1">
								{t("Filter")}
								{totalSelected > 0 && ` (${totalSelected})`}
							</Typography>
							<DialogClose asChild>
								<button
									type="button"
									aria-label={t("Close filters")}
									className="inline-flex min-h-9 min-w-9 cursor-pointer items-center justify-center p-1.5"
								>
									<Close className="size-6" />
								</button>
							</DialogClose>
						</div>
					</header>
					<div className="flex-1 overflow-y-auto px-grid-component-spacing-xl py-grid-component-spacing-2xl">
						<Filters
							filters={filters}
							selectedByKey={selectedByKey}
							availableByKey={availableByKey}
							toggleFilter={toggleFilter}
							isLoading={isLoading}
						/>
						{/* Desktop has its own Sort dropdown in the Filterbar — only render
						the accordion below lg. */}
						<div className="lg:hidden">
							<SortAccordion sorts={sorts} sortId={sortId} setSort={setSort} />
						</div>
					</div>
					<FooterActions
						totalResults={total}
						hasSelections={totalSelected > 0}
						isFetching={isFetching}
						onClear={clear}
						onShowResults={close}
						layout="horizontal"
					/>
				</DrawerContent>
			</DrawerViewport>
		</DrawerRoot>
	);
};

const UnsuspendedFilterModal = () => {
	return <FilterDrawer />;
};

export const FilterModal = () => {
	return (
		<Suspense>
			<UnsuspendedFilterModal />
		</Suspense>
	);
};

export interface FilterModalTriggerProps extends ComponentProps<"button"> {
	asChild?: boolean;
}

const UnsuspendedFilterModalTrigger = ({
	asChild,
	children,
	...props
}: FilterModalTriggerProps) => {
	const { toggle } = useModal("filters");

	const Component = asChild ? Slot : "button";

	return (
		<Component {...props} onClick={toggle}>
			{children}
		</Component>
	);
};

export const FilterModalTrigger: typeof UnsuspendedFilterModalTrigger = (props) => {
	return (
		<Suspense>
			<UnsuspendedFilterModalTrigger {...props} />
		</Suspense>
	);
};
